import type { DayObject } from "@/types/date"

// --- Types ---

export interface BookingLinkData {
  entityId: string
  checkin: string
  checkout: string
  roomRef?: string
}

export interface ReportLinkData {
  reportId: number
}

// FlightOption:N markers in stored content (historical messages and AgentSessionEvents)
// flow through this module's option-typed parsers. New bodies use fq-XXX (FlightQuote)
// and FlightId:N (segment) only. Discriminated union narrows `id` per `type`:
// `option`/`flight` carry numeric IDs (sb_flight_search_results.id / sb_flights.id);
// `quote` carries the FlightQuote.ref string (e.g. "fq-abc12345").
export type FlightLinkData =
  | { type: "option"; id: number }
  | { type: "flight"; id: number }
  | { type: "quote"; id: string }

export interface HotelQuoteLinkData {
  quoteRef: string
}

export interface FlightQuoteLinkData {
  quoteRef: string
}

export type SpecialLinkType =
  | "BOOKINGLINK"
  | "REPORTLINK"
  | "FLIGHTIDLINK"
  | "FLIGHTOPTIONLINK"
  | "HOTELQUOTELINK"
  | "FLIGHTQUOTELINK"

export interface EntityMentionData {
  /** Annotation kind, e.g. "activity/concert_venue" */
  kind: string
  /** Human location label, e.g. "Austin, Texas" */
  locationLabel?: string
  /** Opaque entity ref ("at-U5glys2j") or raw entity id ("Attraction:5734667") */
  entityRef?: string
  name: string
}

export type ParsedMarkerSegment =
  | { type: "text"; content: string }
  | { type: "link"; linkType: SpecialLinkType; url: string; text: string }
  | { type: "entity"; entity: EntityMentionData }

// --- Constants ---

export const MARKER_URL_PATTERNS: Record<SpecialLinkType, RegExp> = {
  BOOKINGLINK: /^(Hotel:\d+,\d{4}-\d{2}-\d{2},\d{4}-\d{2}-\d{2}):(.+)$/,
  REPORTLINK: /^(Report:\d+):(.+)$/i,
  FLIGHTIDLINK: /^(FlightId:\d+):(.+)$/i,
  FLIGHTOPTIONLINK: /^(FlightOption:\d+):(.+)$/i,
  HOTELQUOTELINK: /^(hq-[A-Za-z0-9]+):(.+)$/,
  FLIGHTQUOTELINK: /^(fq-[A-Za-z0-9]+):(.+)$/,
}

export const DEFAULT_LINK_TEXT: Record<SpecialLinkType, string> = {
  BOOKINGLINK: "Book Now",
  REPORTLINK: "View Report",
  FLIGHTIDLINK: "View Flight",
  FLIGHTOPTIONLINK: "View Flight",
  HOTELQUOTELINK: "View Quote",
  FLIGHTQUOTELINK: "View Trip",
}

// --- Link Parsers ---

export function parseBookingLink(href: string): BookingLinkData | null {
  const matchWithRoom = href.match(
    /^ho-([A-Za-z0-9]+),(\d{4}-\d{2}-\d{2}),(\d{4}-\d{2}-\d{2}),rm-([A-Za-z0-9]+)$/
  )
  if (matchWithRoom) {
    return {
      entityId: matchWithRoom[1],
      checkin: matchWithRoom[2],
      checkout: matchWithRoom[3],
      roomRef: `rm-${matchWithRoom[4]}`,
    }
  }

  const matchWithoutRoom = href.match(
    /^ho-([A-Za-z0-9]+),(\d{4}-\d{2}-\d{2}),(\d{4}-\d{2}-\d{2})$/
  )
  if (matchWithoutRoom) {
    return {
      entityId: matchWithoutRoom[1],
      checkin: matchWithoutRoom[2],
      checkout: matchWithoutRoom[3],
    }
  }
  return null
}

export function parseReportLink(href: string): ReportLinkData | null {
  const match = href.match(/^Report:(\d+)$/i)
  if (match) {
    return { reportId: parseInt(match[1], 10) }
  }
  return null
}

/**
 * Parse flight segment/option/quote links from agent response markdown.
 *
 * Three link formats are supported:
 * - `FlightId:ID` — shows hover card with single-segment details. ID is sb_flights.id.
 * - `FlightOption:ID` — historical content only. Links to a full flight itinerary
 *   in messages persisted before the FlightQuote rollout. ID is sb_flight_search_results.id.
 *   New bodies use fq-XXX instead.
 * - `fq-REFERENCE` — links to a FlightQuote bundling 1-N itineraries (hover popover with quote info).
 *   REFERENCE is FlightQuote.ref (e.g. "fq-diunW3JX"). MessageFlightButton dispatches via this
 *   parser; AgentFlightQuoteLink in agent-response-renderer goes through parseFlightQuoteLink directly.
 */
export function parseFlightLink(href: string): FlightLinkData | null {
  const flightMatch = href.match(/^FlightId:(\d+)$/i)
  if (flightMatch) {
    return { id: parseInt(flightMatch[1], 10), type: "flight" }
  }
  const optionMatch = href.match(/^FlightOption:(\d+)$/i)
  if (optionMatch) {
    return { id: parseInt(optionMatch[1], 10), type: "option" }
  }
  const quoteMatch = href.match(/^(fq-[A-Za-z0-9]+)$/)
  if (quoteMatch) {
    return { id: quoteMatch[1], type: "quote" }
  }
  return null
}

/**
 * Parse hotel quote links from agent response markdown.
 *
 * Link format: `hq-REFERENCE` where REFERENCE is the quote reference
 * Example: `hq-4TBOV0mL`
 */
export function parseHotelQuoteLink(href: string): HotelQuoteLinkData | null {
  const match = href.match(/^(hq-[A-Za-z0-9]+)$/)
  if (match) {
    return { quoteRef: match[1] }
  }
  return null
}

/**
 * Parse flight quote links from agent response markdown.
 *
 * Link format: `fq-REFERENCE` where REFERENCE is the quote reference.
 * Example: `fq-abc12345`
 */
export function parseFlightQuoteLink(href: string): FlightQuoteLinkData | null {
  const match = href.match(/^(fq-[A-Za-z0-9]+)$/)
  if (match) {
    return { quoteRef: match[1] }
  }
  return null
}

export function parseDateString(dateStr: string): DayObject {
  const [year, month, day] = dateStr.split("-").map(Number)
  return { year, month, day }
}

// --- Content Preprocessing ---

export function stripMarkdownFormatting(text: string): string {
  return text
    .replace(/\*\*/g, "")
    .replace(/__/g, "")
    .replace(/\*/g, "")
    .replace(/_/g, "")
}

// Agent-report entity annotation:
//   **[activity/concert_venue]{Austin, Texas}(Attraction:5734667)The Concourse Project**
// {location} and the (ref) are both optional; the ref may be either a raw
// entity id or an opaque entity_ref the caller already resolved (at-XXXXXXXX).
const ENTITY_ANNOTATION_REGEX =
  /\*\*\[([a-z_]+(?:\/[a-z_]+)?)\](?:\{([^}\n]*)\})?(?:\(((?:[A-Za-z]+:\d+)|(?:[a-z]{2}-[A-Za-z0-9]{8}))\))?([^*\n]+)\*\*/g

// Field separator inside ENTITYMENTION markers. Must survive markdown parsing
// untouched — annotations appear inside GFM table cells, where a "|" separator
// would be split into table columns.
export const ENTITY_MENTION_SEP = "\u001F"

export function preprocessAgentContent(content: string): string {
  let processed = content

  // Pre-process entity annotation mentions (must run before the link passes
  // below, since the (Type:id) segment would otherwise read as a plain link)
  processed = processed.replace(
    ENTITY_ANNOTATION_REGEX,
    (_match, kind, location, ref, name) => {
      const cleanName = stripMarkdownFormatting(name).trim()
      return `@@ENTITYMENTION:${[kind, location ?? "", ref ?? "", cleanName].join(ENTITY_MENTION_SEP)}@@`
    }
  )

  // Convert <sup> tags to inline code with CITE: prefix
  processed = processed.replace(/<sup>([^<]+)<\/sup>/g, (_match, citation) => {
    return `\`CITE:${citation}\``
  })

  // Handle {{CITATION:...}} format
  processed = processed.replace(
    /\{\{CITATION:([^}]+)\}\}/g,
    (_match, citation) => {
      return `\`CITE:${citation}\``
    }
  )

  // Pre-process booking links
  processed = processed.replace(
    /\[([^\]]+)\]\((Hotel:\d+,\d{4}-\d{2}-\d{2},\d{4}-\d{2}-\d{2})\)/g,
    (_match, text, url) => {
      return `@@BOOKINGLINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  // Pre-process report links
  processed = processed.replace(
    /\[([^\]]+)\]\((Report:\d+)\)/gi,
    (_match, text, url) => {
      return `@@REPORTLINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  // Pre-process flight segment links
  processed = processed.replace(
    /\[([^\]]+)\]\((FlightId:\d+)\)/gi,
    (_match, text, url) => {
      return `@@FLIGHTIDLINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  // Pre-process historical FlightOption: links (stored content only)
  processed = processed.replace(
    /\[([^\]]+)\]\((FlightOption:\d+)\)/gi,
    (_match, text, url) => {
      return `@@FLIGHTOPTIONLINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  // Pre-process hotel quote links
  processed = processed.replace(
    /\[([^\]]+)\]\((hq-[A-Za-z0-9]+)\)/g,
    (_match, text, url) => {
      return `@@HOTELQUOTELINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  // Pre-process flight quote links
  processed = processed.replace(
    /\[([^\]]+)\]\((fq-[A-Za-z0-9]+)\)/g,
    (_match, text, url) => {
      return `@@FLIGHTQUOTELINK:${url}:${stripMarkdownFormatting(text)}@@`
    }
  )

  return processed
}

// --- Marker Parsing ---

export function extractMarkerUrlAndText(
  linkType: string,
  capturedContent: string
): { url: string; text: string } {
  const pattern = MARKER_URL_PATTERNS[linkType as SpecialLinkType]
  const urlTextMatch = pattern ? capturedContent.match(pattern) : null

  if (urlTextMatch) {
    return { url: urlTextMatch[1], text: urlTextMatch[2] }
  }

  return {
    url: capturedContent,
    text: DEFAULT_LINK_TEXT[linkType as SpecialLinkType] ?? "View",
  }
}

function parseEntityMentionMarker(captured: string): EntityMentionData {
  const [kind, locationLabel, entityRef, ...nameParts] =
    captured.split(ENTITY_MENTION_SEP)
  return {
    kind,
    locationLabel: locationLabel || undefined,
    entityRef: entityRef || undefined,
    name: nameParts.join(ENTITY_MENTION_SEP),
  }
}

export function parseMarkerSegments(content: string): ParsedMarkerSegment[] {
  const segments: ParsedMarkerSegment[] = []
  const markerRegex =
    /@@(BOOKINGLINK|REPORTLINK|FLIGHTIDLINK|FLIGHTOPTIONLINK|HOTELQUOTELINK|FLIGHTQUOTELINK|ENTITYMENTION):([^@]+)@@/g
  let lastIndex = 0
  let match

  while ((match = markerRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "text",
        content: content.slice(lastIndex, match.index),
      })
    }

    if (match[1] === "ENTITYMENTION") {
      segments.push({
        type: "entity",
        entity: parseEntityMentionMarker(match[2]),
      })
    } else {
      const linkType = match[1] as SpecialLinkType
      const { url, text } = extractMarkerUrlAndText(linkType, match[2])
      segments.push({ type: "link", linkType, url, text })
    }

    lastIndex = markerRegex.lastIndex
  }

  if (lastIndex < content.length) {
    segments.push({ type: "text", content: content.slice(lastIndex) })
  }

  return segments
}
